/**
 * PATH: packages/quote-builder/src/grid/a1MultiSheet.ts
 * FILE_ID: QB-GRID-A1MULTISHEET-001
 * PURPOSE: Multi-sheet ("tabs") layer over the A1 workbook blob. Pure
 *   normalize/serialize between the persisted JSON shape and the
 *   editor's { sheets[], activeSheetId } model.
 * WHY: ARC-3 follow-up (operator 2026-07-03: "can't add more tabs /
 *   attached sheets.."). The persisted blob must stay BACKWARD- and
 *   FORWARD-compatible: legacy single-sheet blobs ({cells,rows,cols})
 *   normalize into one sheet, and the serialized multi-sheet blob keeps
 *   mirroring sheet 1 at the top level so older FE bundles (and the
 *   backend, which treats the blob as opaque) still read a valid
 *   single-sheet workbook.
 * FLOW:
 *   persisted JSON → normalizeA1MultiSheet → { sheets, activeSheetId }
 *   { sheets, activeSheetId } → serializeA1MultiSheet → persisted JSON
 * DEPENDENCIES:
 *   - ./a1Workbook: normalizeA1Workbook (per-sheet cell/rows/cols rules)
 */
import { normalizeA1Workbook, type A1WorkbookData } from './a1Workbook'
import { parseRef } from './a1'

export interface A1WorkbookSheet extends A1WorkbookData {
  id: string
  name: string
  /** MR5 (planning) — A1 refs the operator locked against editing. */
  locked: string[]
  /** MR5 (planning) — 1-based header row (bold + feeds column headers), or null. */
  headerRow: number | null
}

export interface A1MultiSheetWorkbook {
  sheets: A1WorkbookSheet[]
  activeSheetId: string
}

const MAX_SHEETS = 12
const MAX_SHEET_NAME = 40

/** Coerce an arbitrary name value into a bounded display name. */
function cleanSheetName(v: unknown, fallback: string): string {
  if (typeof v !== 'string') return fallback
  const t = v.trim().slice(0, MAX_SHEET_NAME)
  return t === '' ? fallback : t
}

/** MR5 — coerce a persisted `locked` value into valid, deduped A1 refs. */
function cleanLockedRefs(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  const out = new Set<string>()
  for (const entry of v) {
    if (typeof entry === 'string' && parseRef(entry)) out.add(entry.toUpperCase())
  }
  return [...out]
}

/** MR5 — coerce a persisted `header_row` into a 1-based row within bounds. */
function cleanHeaderRow(v: unknown, rows: number): number | null {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= rows ? v : null
}

/**
 * Pure: persisted blob (any shape) → editor model.
 *
 * - `{sheets: [...]}` blobs normalize each entry via the single-sheet
 *   rules (invalid refs dropped, rows/cols clamped) with stable ids.
 * - Legacy `{cells,rows,cols}` blobs (or junk) wrap into ONE sheet.
 * - `active_sheet_id` is honoured when it matches a sheet, else sheet 1.
 */
export function normalizeA1MultiSheet(raw: unknown): A1MultiSheetWorkbook {
  const obj = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>
  const rawSheets = Array.isArray(obj.sheets) ? obj.sheets.slice(0, MAX_SHEETS) : null

  let sheets: A1WorkbookSheet[]
  if (rawSheets && rawSheets.length > 0) {
    sheets = rawSheets.map((entry, idx) => {
      const s = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>
      const base = normalizeA1Workbook(s)
      return {
        id: typeof s.id === 'string' && s.id !== '' ? s.id : `sheet-${idx + 1}`,
        name: cleanSheetName(s.name, `Sheet ${idx + 1}`),
        ...base,
        locked: cleanLockedRefs(s.locked),
        headerRow: cleanHeaderRow(s.header_row, base.rows),
      }
    })
  } else {
    const base = normalizeA1Workbook(obj)
    sheets = [{
      id: 'sheet-1',
      name: 'Sheet 1',
      ...base,
      locked: cleanLockedRefs(obj.locked),
      headerRow: cleanHeaderRow(obj.header_row, base.rows),
    }]
  }

  const requested = typeof obj.active_sheet_id === 'string' ? obj.active_sheet_id : ''
  const activeSheetId = sheets.some((s) => s.id === requested)
    ? requested
    : sheets[0].id
  return { sheets, activeSheetId }
}

/**
 * PERF-2026-07-09-WORKBOOK-SLIM (load-speed audit R4) — drop
 * empty-string cell entries from a cell map before persisting. A
 * cleared cell leaves a `"B3": ""` tombstone in the editor model;
 * on big grids these tombstones dominated the persisted blob (the
 * GET/PUT p95 tail of 25-32s was transfer size — server p50 was
 * 65-170ms). A missing key and an empty string are the same cell to
 * every reader (getCell falls back to ''), so stripping is contract-
 * preserving. Pure I→O; returns the SAME reference when nothing to
 * strip so unchanged sheets don't churn.
 */
export function stripEmptyCells(
  cells: Record<string, string>,
): Record<string, string> {
  let hasEmpty = false
  for (const k in cells) {
    if (cells[k] === '') { hasEmpty = true; break }
  }
  if (!hasEmpty) return cells
  const out: Record<string, string> = {}
  for (const k in cells) {
    if (cells[k] !== '') out[k] = cells[k]
  }
  return out
}

/**
 * Pure: editor model → persisted blob.
 *
 * Sheet 1's cells/rows/cols are MIRRORED at the top level so any legacy
 * reader (older FE bundle mid-session, external tooling) still sees a
 * valid single-sheet workbook. `sheets` + `active_sheet_id` carry the
 * full model for multi-sheet-aware readers.
 *
 * Empty-string cells are stripped both at the mirror and per sheet
 * (see stripEmptyCells) — the blob only carries content-bearing cells.
 */
export function serializeA1MultiSheet(
  wb: A1MultiSheetWorkbook,
): Record<string, unknown> {
  const first = wb.sheets[0]
  return {
    cells: stripEmptyCells(first?.cells ?? {}),
    rows: first?.rows ?? 24,
    cols: first?.cols ?? 8,
    sheets: wb.sheets.map((s) => ({
      id: s.id,
      name: s.name,
      cells: stripEmptyCells(s.cells),
      rows: s.rows,
      cols: s.cols,
      // MR5 (planning) — locks + header row persist per sheet. Legacy
      // readers ignore the extra keys (the top-level mirror stays the
      // plain {cells,rows,cols} contract).
      locked: s.locked,
      header_row: s.headerRow,
    })),
    active_sheet_id: wb.activeSheetId,
  }
}

/** Pure: next unique sheet (id + default name) to append. */
export function makeNewSheet(existing: A1WorkbookSheet[]): A1WorkbookSheet | null {
  if (existing.length >= MAX_SHEETS) return null
  const usedIds = new Set(existing.map((s) => s.id))
  let n = existing.length + 1
  while (usedIds.has(`sheet-${n}`)) n++
  return {
    id: `sheet-${n}`,
    name: `Sheet ${n}`,
    cells: {},
    rows: 24,
    cols: 8,
    locked: [],
    headerRow: null,
  }
}

export const A1_MULTISHEET_MAX_SHEETS = MAX_SHEETS
